import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { lookupInvite, acceptInvite } from '../lib/api'
import { useAuth, portalPathFor } from '../lib/auth'
import { PHONE_DISPLAY, PHONE_TEL } from '../lib/constants'

export default function SetPassword() {
  const [params] = useSearchParams()
  const token = params.get('token') || ''
  const navigate = useNavigate()
  const { refresh } = useAuth()

  const [state, setState] = useState({ status: 'checking', invite: null, error: '' })
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [formError, setFormError] = useState('')
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    let cancelled = false
    if (!token) {
      setState({ status: 'invalid', invite: null, error: 'This link is missing its code.' })
      return
    }
    lookupInvite(token)
      .then((invite) => !cancelled && setState({ status: 'ready', invite, error: '' }))
      .catch((err) => !cancelled && setState({ status: 'invalid', invite: null, error: err.message }))
    return () => {
      cancelled = true
    }
  }, [token])

  const submit = async (e) => {
    e.preventDefault()
    if (busy) return
    if (password !== confirm) {
      setFormError('The two passwords do not match.')
      return
    }
    setBusy(true)
    try {
      const { user } = await acceptInvite(token, password)
      await refresh()
      navigate(portalPathFor(user.role), { replace: true })
    } catch (err) {
      setFormError(err.message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <section className="section">
      <div className="container-site">
        <div style={{ maxWidth: 520 }}>
          {state.status === 'checking' && <p style={{ color: 'var(--color-muted)' }}>Checking your link…</p>}

          {state.status === 'invalid' && (
            <>
              <p className="eyebrow">Portal access</p>
              <h1 className="h1-hero" style={{ marginTop: 12 }}>This link can't be used.</h1>
              <p style={{ margin: '20px 0 0 0', fontSize: 17, lineHeight: 1.7, color: 'var(--color-muted)' }}>
                {state.error} Setup links expire after seven days and can only be used once. Please call the office on{' '}
                <a href={PHONE_TEL} style={{ color: 'var(--color-teal-800)', fontWeight: 600 }}>{PHONE_DISPLAY}</a> and
                we'll send you a fresh one.
              </p>
            </>
          )}

          {state.status === 'ready' && (
            <>
              <p className="eyebrow">{state.invite.isReset ? 'Reset your password' : 'Welcome to True North'}</p>
              <h1 className="h1-hero" style={{ marginTop: 12 }}>
                {state.invite.isReset ? 'Choose a new password' : 'Choose your password'}
              </h1>
              <p style={{ margin: '20px 0 0 0', fontSize: 17, lineHeight: 1.7, color: 'var(--color-muted)' }}>
                Setting up the account for <strong style={{ color: 'var(--color-teal-900)' }}>{state.invite.email}</strong>.
                Only you will know this password — no one at True North can see it.
              </p>

              <form
                onSubmit={submit}
                style={{
                  marginTop: 32,
                  borderRadius: 8,
                  border: '1px solid var(--color-line)',
                  background: '#FFFFFF',
                  padding: 'clamp(24px, 5vw, 32px)',
                  boxShadow: 'var(--shadow-soft)',
                }}
              >
                <div style={{ marginBottom: 20 }}>
                  <label className="field-label" htmlFor="sp-password">New password</label>
                  <input
                    id="sp-password"
                    className="field-input"
                    type="password"
                    autoComplete="new-password"
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value)
                      setFormError('')
                    }}
                  />
                  <p style={{ margin: '8px 0 0 0', fontSize: 15, color: 'var(--color-muted)' }}>
                    At least 12 characters, including a letter and a number.
                  </p>
                </div>

                <div style={{ marginBottom: 20 }}>
                  <label className="field-label" htmlFor="sp-confirm">Confirm password</label>
                  <input
                    id="sp-confirm"
                    className="field-input"
                    type="password"
                    autoComplete="new-password"
                    value={confirm}
                    onChange={(e) => {
                      setConfirm(e.target.value)
                      setFormError('')
                    }}
                  />
                </div>

                {formError && (
                  <p className="field-error" role="alert" style={{ marginBottom: 16 }}>
                    {formError}
                  </p>
                )}

                <button type="submit" className="btn btn-primary" disabled={busy} style={{ width: '100%' }}>
                  {busy ? 'Saving…' : 'Save password and sign in'}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </section>
  )
}
